import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { communicationsAPI, phoneSystemAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Phone, ArrowRight, ArrowLeft, Check, Loader2, Search,
  Zap, Shield, Clock, Users, Star, PhoneIncoming, Voicemail,
  PhoneOff, MessageSquare, Mic, MapPin,
} from 'lucide-react';


const TIERS = {
  voip: { name: 'VoIP', price: 49, minutes: 500, lines: 2, overage: 0.06, queue: false },
  voip_advanced: { name: 'VoIP Advanced', price: 149, minutes: 2500, lines: 10, overage: 0.06, queue: true },
  voip_premium: { name: 'VoIP Premium', price: 299, minutes: 7500, lines: 30, overage: 0.04, queue: true },
};


// ═══════════════════════════════════════════════════════════
// STEP 1: Plan Confirmation
// ═══════════════════════════════════════════════════════════

function PlanStep({ setupState, onSelectTier, onNext }) {
  const [selectedTier, setSelectedTier] = useState(setupState?.tier || 'voip');
  const [saving, setSaving] = useState(false);
  const [downgradeWarning, setDowngradeWarning] = useState(null);

  const handleConfirm = async (forceConfirm = false) => {
    setSaving(true);
    setDowngradeWarning(null);
    try {
      const res = await communicationsAPI.selectTier(selectedTier, forceConfirm);
      if (res.data.requires_confirmation && !forceConfirm) {
        setDowngradeWarning(res.data.downgrade_warning);
        setSaving(false);
        return;
      }
      if (res.data.downgrade_warning && res.data.applied) {
        toast.info(res.data.downgrade_warning);
      }
      onSelectTier(selectedTier);
      onNext();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="plan-step">
      <div>
        <h2 className="text-lg font-semibold">Choose Your Phone Plan</h2>
        <p className="text-sm text-muted-foreground">Select the plan that fits your business needs.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {Object.entries(TIERS).map(([key, tier]) => {
          const active = selectedTier === key;
          return (
            <Card key={key} className={`cursor-pointer transition-all ${active ? 'ring-2 ring-blue-500 shadow-md' : 'hover:shadow-sm'}`}
              onClick={() => setSelectedTier(key)} data-testid={`tier-${key}`}>
              <CardContent className="pt-5 pb-4 px-4">
                {key === 'voip_advanced' && (
                  <Badge className="bg-blue-100 text-blue-700 text-[10px] mb-2">Most Popular</Badge>
                )}
                <p className="text-base font-semibold">{tier.name}</p>
                <p className="text-2xl font-bold mt-1">${tier.price}<span className="text-sm font-normal text-muted-foreground">/mo</span></p>
                <div className="mt-3 space-y-1.5 text-xs text-muted-foreground">
                  <p className="flex items-center gap-1.5"><Phone className="w-3 h-3" />{tier.lines} included lines</p>
                  <p className="flex items-center gap-1.5"><Clock className="w-3 h-3" />{tier.minutes.toLocaleString()} included minutes</p>
                  <p className="flex items-center gap-1.5"><Zap className="w-3 h-3" />${tier.overage}/min overage</p>
                  <p className="flex items-center gap-1.5"><MessageSquare className="w-3 h-3" />Unlimited SMS</p>
                  {tier.queue && <p className="flex items-center gap-1.5"><Users className="w-3 h-3" />Call queueing</p>}
                  <p className="flex items-center gap-1.5"><Shield className="w-3 h-3" />$13/mo per extra line</p>
                </div>
                {active && <div className="mt-3 flex items-center gap-1 text-blue-600 text-xs font-medium"><Check className="w-3.5 h-3.5" />Selected</div>}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {downgradeWarning && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3" data-testid="downgrade-warning">
          <div className="flex items-start gap-2">
            <Shield className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-900">Plan Downgrade Warning</p>
              <p className="text-sm text-amber-800 mt-1">{downgradeWarning}</p>
              <p className="text-xs text-amber-700 mt-2">Extra lines will continue to be billed. You can release numbers in Communications Settings if needed.</p>
            </div>
          </div>
          <div className="flex gap-2 ml-7">
            <Button size="sm" variant="outline" onClick={() => setDowngradeWarning(null)} data-testid="cancel-downgrade-btn">
              Choose Different Plan
            </Button>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={() => handleConfirm(true)} disabled={saving} data-testid="confirm-downgrade-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Confirm Downgrade
            </Button>
          </div>
        </div>
      )}

      {!downgradeWarning && (
        <Button onClick={() => handleConfirm()} disabled={saving} data-testid="confirm-plan-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
          Confirm Plan <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// STEP 2: Number Acquisition
// ═══════════════════════════════════════════════════════════

function NumbersStep({ onNext, onBack }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState(null); // null | 'search' | 'port' | 'skip'
  const [areaCode, setAreaCode] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [purchasing, setPurchasing] = useState(null);
  const [purchased, setPurchased] = useState([]); // track purchased numbers

  const handleSearch = async () => {
    if (!areaCode || areaCode.length < 3) {
      toast.error('Enter a 3-digit area code');
      return;
    }
    setSearching(true);
    try {
      const res = await phoneSystemAPI.searchNumbers({ area_code: areaCode, limit: 10 });
      const results = Array.isArray(res.data) ? res.data : (res.data.numbers || []);
      setSearchResults(results);
      if (results.length === 0) toast.info('No numbers found for that area code');
    } catch {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handlePurchase = async (number) => {
    setPurchasing(number.phone_number);
    try {
      const res = await phoneSystemAPI.purchaseNumber({ phone_number: number.phone_number });
      toast.success(`Purchased ${number.friendly_name || number.phone_number}`);
      if (res.data.extra_line_warning) {
        toast.info(`Billing note: ${res.data.extra_line_warning}`, { duration: 6000 });
      }
      setPurchased(prev => [...prev, number.phone_number]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Purchase failed');
    } finally {
      setPurchasing(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="numbers-step">
      <div>
        <h2 className="text-lg font-semibold">Get Phone Numbers</h2>
        <p className="text-sm text-muted-foreground">Search for new numbers or port your existing business numbers.</p>
      </div>

      {mode === null && (
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="cursor-pointer hover:shadow-sm hover:border-blue-200 transition-all" onClick={() => setMode('search')} data-testid="get-new-btn">
            <CardContent className="pt-5 pb-4 text-center">
              <Search className="w-8 h-8 mx-auto mb-2 text-blue-500" />
              <p className="text-sm font-medium">Get New Numbers</p>
              <p className="text-xs text-muted-foreground mt-1">Search by area code</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-sm hover:border-green-200 transition-all" onClick={() => navigate('/settings/phone-numbers?tab=porting&startWizard=true')} data-testid="port-btn">
            <CardContent className="pt-5 pb-4 text-center">
              <ArrowRight className="w-8 h-8 mx-auto mb-2 text-green-500" />
              <p className="text-sm font-medium">Port Existing Numbers</p>
              <p className="text-xs text-muted-foreground mt-1">Transfer from another carrier</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-sm hover:border-muted transition-all" onClick={() => { setMode('skip'); onNext(); }} data-testid="skip-btn">
            <CardContent className="pt-5 pb-4 text-center">
              <Clock className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm font-medium">Finish Later</p>
              <p className="text-xs text-muted-foreground mt-1">Add numbers anytime in settings</p>
            </CardContent>
          </Card>
        </div>
      )}

      {mode === 'search' && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Search Available Numbers</CardTitle>
            <CardDescription className="text-xs">Enter an area code to find available phone numbers in your region.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input value={areaCode} onChange={e => setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
                placeholder="Area code (e.g. 941)" className="max-w-[180px] text-sm" data-testid="area-code-input"
                onKeyDown={e => e.key === 'Enter' && handleSearch()} />
              <Button size="sm" onClick={handleSearch} disabled={searching} data-testid="search-btn">
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
                Search
              </Button>
            </div>

            {purchased.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3" data-testid="purchased-summary">
                <p className="text-xs font-medium text-green-800 mb-1">Numbers acquired ({purchased.length})</p>
                <div className="flex flex-wrap gap-1.5">
                  {purchased.map(p => (
                    <span key={p} className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-800 rounded-full px-2.5 py-0.5">
                      <Check className="w-3 h-3" />{p}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {searchResults.length > 0 && (
              <div className="divide-y rounded-lg border">
                {searchResults.map(n => {
                  const isPurchased = purchased.includes(n.phone_number);
                  return (
                    <div key={n.phone_number} className={`flex items-center justify-between px-3 py-2.5 ${isPurchased ? 'bg-green-50/50' : ''}`} data-testid={`search-result-${n.phone_number}`}>
                      <div className="flex items-center gap-2">
                        {isPurchased && <Check className="w-4 h-4 text-green-600 shrink-0" />}
                        <div>
                          <p className="text-sm font-medium">{n.friendly_name || n.phone_number}</p>
                          {n.locality && <p className="text-xs text-muted-foreground">{n.locality}, {n.region}</p>}
                        </div>
                      </div>
                      {isPurchased ? (
                        <Badge className="bg-green-100 text-green-700 text-[10px]">Acquired</Badge>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => handlePurchase(n)} disabled={purchasing === n.phone_number} data-testid={`buy-${n.phone_number}`}>
                          {purchasing === n.phone_number ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Get This Number'}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="flex gap-2 pt-1">
        <Button variant="outline" onClick={onBack} data-testid="back-btn"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
        {mode && mode !== 'skip' && mode !== 'port' && (
          <Button onClick={onNext} data-testid="next-btn">Continue <ArrowRight className="w-4 h-4 ml-1" /></Button>
        )}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// STEP 3: Call Flow Setup
// ═══════════════════════════════════════════════════════════

function CallFlowStep({ onNext, onBack }) {
  const [flows, setFlows] = useState([]);
  const [numbers, setNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    Promise.all([
      communicationsAPI.callFlows(),
      communicationsAPI.lineSummary(),
    ]).then(([fRes, lRes]) => {
      setFlows(fRes.data);
      setNumbers(lRes.data.numbers || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleCreateDefault = async () => {
    setCreating(true);
    try {
      const res = await communicationsAPI.createCallFlow({
        name: 'Main Line',
        is_default: true,
      });
      // Assign all numbers to it
      const numIds = numbers.map(n => n.id);
      if (numIds.length > 0) {
        await communicationsAPI.assignNumbers(res.data.id, numIds);
      }
      toast.success('Default call flow created and numbers assigned');
      setFlows([res.data]);
    } catch {
      toast.error('Failed to create call flow');
    } finally {
      setCreating(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="call-flow-step">
      <div>
        <h2 className="text-lg font-semibold">Set Up Call Routing</h2>
        <p className="text-sm text-muted-foreground">Create a default call flow so incoming calls reach your team.</p>
      </div>

      {flows.length > 0 ? (
        <Card>
          <CardContent className="py-4 space-y-3">
            {flows.map(f => (
              <div key={f.id} className="flex items-center gap-3">
                <Check className="w-5 h-5 text-green-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{f.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {f.steps_json?.length || 0} steps{f.is_default ? ' · Default' : ''} · {f.assigned_number_ids?.length || 0} number(s) assigned
                  </p>
                </div>
                {f.is_default && <Badge className="bg-blue-100 text-blue-700 text-[10px] shrink-0">Default</Badge>}
              </div>
            ))}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-6 text-center">
            <PhoneIncoming className="w-8 h-8 mx-auto mb-2 text-muted-foreground/40" />
            <p className="text-sm mb-1">No call flow configured</p>
            <p className="text-xs text-muted-foreground mb-3">We will create a default flow: Incoming → Check Hours → Ring Team → Voicemail</p>
            <Button size="sm" onClick={handleCreateDefault} disabled={creating} data-testid="create-default-flow-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Create Default Call Flow
            </Button>
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-muted-foreground">You can customize your call flows anytime in Communications Settings → Voice.</p>

      <div className="flex gap-2">
        <Button variant="outline" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
        <Button onClick={onNext}>Continue <ArrowRight className="w-4 h-4 ml-1" /></Button>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// STEP 4: Review & Complete
// ═══════════════════════════════════════════════════════════

function ReviewStep({ tier, onBack, onComplete }) {
  const [completing, setCompleting] = useState(false);
  const [summary, setSummary] = useState(null);
  const [flows, setFlows] = useState([]);

  useEffect(() => {
    Promise.all([
      communicationsAPI.lineSummary(),
      communicationsAPI.callFlows(),
    ]).then(([lRes, fRes]) => {
      setSummary(lRes.data);
      setFlows(fRes.data);
    }).catch(() => {});
  }, []);

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await communicationsAPI.markSetupComplete(true);
      toast.success('Phone setup complete!');
      onComplete();
    } catch {
      toast.error('Failed to mark setup complete');
    } finally {
      setCompleting(false);
    }
  };

  const t = TIERS[tier] || TIERS.voip;
  const defaultFlow = flows.find(f => f.is_default);

  return (
    <div className="space-y-5" data-testid="review-step">
      <div>
        <h2 className="text-lg font-semibold">Review & Complete Setup</h2>
        <p className="text-sm text-muted-foreground">Everything looks good. Review your configuration below.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Plan</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1">
            <p className="font-medium">{t.name} — ${t.price}/mo</p>
            <p className="text-xs text-muted-foreground">{t.lines} lines · {t.minutes.toLocaleString()} min · ${t.overage}/min overage</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Phone Numbers</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1">
            <p className="font-medium">{summary?.assigned_lines || 0} number(s) assigned</p>
            <p className="text-xs text-muted-foreground">{summary?.remaining_lines ?? t.lines} included lines remaining</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Call Routing</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1">
            {defaultFlow ? (
              <>
                <p className="font-medium">{defaultFlow.name}</p>
                <p className="text-xs text-muted-foreground">{defaultFlow.steps_json?.length || 0} steps · {defaultFlow.assigned_number_ids?.length || 0} numbers routed</p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">No call flow configured yet. You can set this up in settings.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <p className="text-sm font-medium text-green-800">After completing setup:</p>
        <ul className="text-xs text-green-700 mt-2 space-y-1.5 ml-4 list-disc">
          <li>Your Contact Center will be fully operational</li>
          <li>Incoming calls will route through your call flow</li>
          <li>The dialer is ready for outbound calls</li>
          <li>Manage all settings in Communications Settings</li>
        </ul>
      </div>

      <div className="flex gap-2 pt-1">
        <Button variant="outline" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
        <Button onClick={handleComplete} disabled={completing} data-testid="complete-setup-btn">
          {completing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
          Complete Setup
        </Button>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// MAIN SETUP PAGE
// ═══════════════════════════════════════════════════════════

const STEPS = ['Plan', 'Numbers', 'Call Flow', 'Review'];

export default function PhoneSetup() {
  const [step, setStep] = useState(0);
  const [setupState, setSetupState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState('voip');

  useEffect(() => {
    communicationsAPI.setupState().then(r => {
      setSetupState(r.data);
      setSelectedTier(r.data.tier || 'voip');
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin" /></div>
      </Layout>
    );
  }

  if (!setupState?.enabled) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-16" data-testid="phone-not-enabled">
          <div className="text-center">
            <Phone className="w-12 h-12 mx-auto mb-4 text-muted-foreground/40" />
            <h2 className="text-lg font-semibold mb-2">Phone System Not Enabled</h2>
            <p className="text-sm text-muted-foreground mb-4">Enable the Phone System add-on in Billing first.</p>
            <Button onClick={() => window.location.href = '/billing'}>Go to Billing</Button>
          </div>
        </div>
      </Layout>
    );
  }

  if (setupState?.setup_complete) {
    return (
      <Layout>
        <div className="space-y-6" data-testid="phone-setup">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-600/10 rounded-lg flex items-center justify-center shrink-0">
              <Phone className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold">Change Phone Plan</h1>
              <p className="text-xs sm:text-sm text-muted-foreground">Switch your plan tier. Your numbers and call flows will be preserved.</p>
            </div>
          </div>

          <PlanStep setupState={setupState} onSelectTier={() => {}} onNext={() => { toast.success('Plan updated'); window.location.href = '/settings/communications'; }} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="phone-setup">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-600/10 rounded-lg flex items-center justify-center shrink-0">
            <Phone className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold">Phone System Setup</h1>
            <p className="text-xs sm:text-sm text-muted-foreground">Configure your business phone system</p>
          </div>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-1 sm:gap-2">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-medium transition-colors ${i <= step ? 'bg-blue-600 text-white' : 'bg-muted text-muted-foreground'}`}>
                  {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
                </div>
                <span className={`text-xs sm:text-sm hidden sm:inline ${i <= step ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < step ? 'bg-blue-600' : 'bg-muted'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step content */}
        {step === 0 && <PlanStep setupState={setupState} onSelectTier={setSelectedTier} onNext={() => setStep(1)} />}
        {step === 1 && <NumbersStep onNext={() => setStep(2)} onBack={() => setStep(0)} />}
        {step === 2 && <CallFlowStep onNext={() => setStep(3)} onBack={() => setStep(1)} />}
        {step === 3 && <ReviewStep tier={selectedTier} onBack={() => setStep(2)} onComplete={() => window.location.href = '/settings/communications'} />}
      </div>
    </Layout>
  );
}
